import Lottie from "lottie-react";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { toast } from "react-toastify";
import * as loadingDots from "../../animations/loading-dots.json";
import diagnosisLookup from "../../options/alabamadiagnosis.json";
import {
  getFieldName,
  getFieldText,
  getFieldUTC,
} from "../../scripts/helperScripts";
import ScrollToTop from "../buttons/ScrollToTop";
import FormCheckbox from "../common/FormCheckbox";
import FormRadioButton from "../common/FormRadioButton";
import FormSelect from "../common/FormSelect";
import FormTextArea from "../common/FormTextArea";
import {
  confirmService,
  confirmServices,
  deactivateService,
  getClientDiagnosisDetails,
  getClientsByDivisionForApproval,
  getClientsByDivisionForApprovalWithTraining,
  getServicesForApprovalByClient,
  getServicesForApprovalByClientWithTraining,
  getServicesForClientsByDates,
  revertDraft,
  saveMessage,
} from "../services/soar";
import ServiceMessages from "../messages/ServiceMessages";
import "../../styles/components/Inbox.css";

const FPApprovals = () => {
  const [data, setData] = useState({
    Division: "",
    Client: "",
  });
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [clientDx, setClientDx] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [serviceUnderReview, setServiceUnderReview] = useState();
  const [serviceToDeactivate, setServiceToDeactivate] = useState();
  const [showDeactivated, setShowDeactivated] = useState(false);
  const [loadStatus, setLoadStatus] = useState({});
  const [errors, setErrors] = useState({});
  const [floatingButtonsVisible, setFloatingButtonsVisible] = useState(false);
  const [toolboxCollapsed, setToolboxCollapsed] = useState(false);
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));

  // const divisionOptions = [{ _id: "Testing", name: "Testing" }];
  const divisionOptions = useMemo(() => {
    if (!safyInfo.user?.divisions) {
      return [];
    }

    return safyInfo.user.divisions.map((d) => ({ _id: d, name: d }));
  }, [safyInfo]);

  const clientOptions = useMemo(() => {
    return clients.map((c) => ({ _id: c.clientId, name: c.clientName }));
  }, [clients]);

  const reviewTypeOptions = [
    { _id: "Notify", name: "Notify of Issue" },
    { _id: "Delete", name: "Delete" },
  ];

  const handleDivisionChange = async ({ currentTarget: input }) => {
    setData((d) => ({ ...d, Division: input.value }));
    setClients([]);
    setLoadStatus((ls) => ({ ...ls, loadingClients: true }));

    const response = await getClientsByDivisionForApproval(input.value);

    setClients(
      response?.sort((a, b) => a.clientName.localeCompare(b.clientName)) || []
    );
    setLoadStatus((ls) => ({ ...ls, loadingClients: false }));
  };

  const handleClientChange = async ({ currentTarget: input }) => {
    setData((d) => ({ ...d, Client: input.value }));
    setServices([]);
    setLoadStatus((ls) => ({ ...ls, loadingServices: true }));

    const promises = [
      getServicesForApprovalByClient(input.value),
      getClientDiagnosisDetails(input.value),
    ];

    const responses = await Promise.all(promises);
    const serviceResponse = responses[0];
    const diagnosisResponse = responses[1];

    setServices(
      serviceResponse
        .sort((a, b) => {
          let aStart;
          let bStart;

          if (a.ServiceStartTime) {
            if (a.ServiceStartTime.endsWith("M")) {
              aStart = moment(
                `${a.ServiceDate} ${a.ServiceStartTime}`,
                "YYYY-MM-DD hh:mm A"
              );
            } else {
              aStart = moment(`${a.ServiceDate}T${a.ServiceStartTime}`);
            }
          } else {
            aStart = moment(a.ServiceDate);
          }

          if (b.ServiceStartTime) {
            if (b.ServiceStartTime.endsWith("M")) {
              bStart = moment(
                `${b.ServiceDate} ${b.ServiceStartTime}`,
                "YYYY-MM-DD hh:mm A"
              );
            } else {
              bStart = moment(`${b.ServiceDate}T${b.ServiceStartTime}`);
            }
          } else {
            bStart = moment(b.ServiceDate);
          }

          return aStart.isBefore(bStart) ? 1 : aStart.isAfter(bStart) ? -1 : 0;
        })
        .map((s) => {
          const firstDx = diagnosisResponse.find((d) => {
            if (
              getFieldText(d.ccx_clientprogram) !== `{${s.ClientProgramId}}`
            ) {
              return false;
            }

            const effDate = moment(getFieldUTC(d.ccx_effdate)).startOf("day");
            const serviceDate = moment(s.ServiceDate);

            if (effDate.isAfter(serviceDate)) {
              return false;
            }

            if (d.ccx_expdate) {
              const expDate = moment(getFieldUTC(d.ccx_expdate)).startOf("day");

              if (expDate.isBefore(serviceDate)) {
                return false;
              }
            }

            const dxCode = d["dx.ccx_code"];

            if (!dxCode.startsWith("F")) {
              if (s.ClientProgramName.includes("Alabama")) {
                return dxCode === "Z65.9";
              }

              if (s.ClientProgramName.includes("Nevada")) {
                return dxCode === "Z03.89";
              }

              return false;
            }

            const dxNumber = parseFloat(dxCode.substring(1));

            if (dxNumber === 43.8) {
              return false;
            } else if (dxNumber >= 1.5 && dxNumber < 70) {
              return true;
            } else if (dxNumber >= 90 && dxNumber < 100) {
              return true;
            }

            return false;
          });

          return {
            ...s,
            Diagnosis: firstDx
              ? getFieldName(firstDx.ccx_clientdiagnosis)
              : undefined,
          };
        })
    );

    setClientDx(diagnosisResponse);

    setLoadStatus((ls) => ({ ...ls, loadingServices: false }));
  };

  const filteredServices = useMemo(() => {
    if (showDeactivated) {
      return services;
    }

    return services.filter((s) => s.Active);
  }, [services, showDeactivated]);

  const handleServiceChange = (recordedServiceId) => {
    const value = [...selectedServices];
    const index = value.indexOf(recordedServiceId);

    if (index === -1) {
      value.push(recordedServiceId);
    } else {
      value.splice(index, 1);
    }

    setSelectedServices(value);
  };

  const handleReviewChange = ({ currentTarget: input }) => {
    setServiceUnderReview((s) => ({ ...s, [input.name]: input.value }));
  };

  const handleSend = () => {
    if (!serviceUnderReview.Type) {
      setErrors((e) => ({ ...e, Type: "Type is required" }));
      return;
    }

    if (!serviceUnderReview.Message) {
      setErrors((e) => ({ ...e, Message: "Message is required" }));
      return;
    }

    const title =
      serviceUnderReview.Type === "Notify"
        ? "Issue with Service"
        : "Delete Service";
    const status =
      serviceUnderReview.Type === "Notify" ? "ServiceMessage" : "ServiceDelete";
    const message = {
      RecordedServiceId: serviceUnderReview.RecordedServiceId,
      ClientId: serviceUnderReview.ClientId,
      ClientName: serviceUnderReview.ClientName,
      TeamMemberId: safyInfo.user.safyInfo.SOARUserId,
      TeamMemberName: `${safyInfo.user.surname}, ${safyInfo.user.givenName}`,
      FosterParentId: serviceUnderReview.AssociatedUserId,
      FosterParentName: serviceUnderReview.AssociatedUserName,
      DiscussionTitle: title,
      DiscussionBody: serviceUnderReview.Message,
      DiscussionStatus: status,
      DiscussionItemStatus: "Sent",
      CreatedById: safyInfo.user.safyInfo.SOARUserId,
      CreatedByName: `${safyInfo.user.surname}, ${safyInfo.user.givenName}`,
    };

    saveMessage(message, "Create");
    if (serviceUnderReview.Type === "Notify") {
      revertDraft(serviceUnderReview.RecordedServiceId);
    } else if (serviceUnderReview.Type === "Delete") {
      deactivateService(serviceUnderReview.RecordedServiceId);
    }

    setServices((s) => {
      const arr = [...s];
      arr.splice(
        arr.findIndex(
          (item) =>
            item.RecordedServiceId === serviceUnderReview.RecordedServiceId
        ),
        1
      );
      return arr;
    });
    setServiceUnderReview();
  };

  const validateServiceDate = useCallback(
    async (service) => {
      const selected = service
        ? [service]
        : services
            .filter((s) => selectedServices.includes(`${s.RecordedServiceId}`))
            .sort((a, b) => {
              let aStart;
              let bStart;

              if (a.ServiceStartTime) {
                if (a.ServiceStartTime.endsWith("M")) {
                  aStart = moment(
                    `${a.ServiceDate} ${a.ServiceStartTime}`,
                    "YYYY-MM-DD hh:mm A"
                  );
                } else {
                  aStart = moment(`${a.ServiceDate}T${a.ServiceStartTime}`);
                }
              } else {
                aStart = moment(a.ServiceDate);
              }

              if (b.ServiceStartTime) {
                if (b.ServiceStartTime.endsWith("M")) {
                  bStart = moment(
                    `${b.ServiceDate} ${b.ServiceStartTime}`,
                    "YYYY-MM-DD hh:mm A"
                  );
                } else {
                  bStart = moment(`${b.ServiceDate}T${b.ServiceStartTime}`);
                }
              } else {
                bStart = moment(b.ServiceDate);
              }

              return aStart.isBefore(bStart)
                ? 1
                : aStart.isAfter(bStart)
                ? -1
                : 0;
            });

      let startTime;
      let endTime;

      if (selected[selected.length - 1].ServiceStartTime) {
        if (selected[selected.length - 1].ServiceStartTime.endsWith("M")) {
          startTime = moment(
            `${selected[selected.length - 1].ServiceDate} ${
              selected[selected.length - 1].ServiceStartTime
            }`,
            "YYYY-MM-DD hh:mm A"
          );
        } else {
          startTime = moment(
            `${selected[selected.length - 1].ServiceDate}T${
              selected[selected.length - 1].ServiceStartTime
            }`
          );
        }
      } else {
        startTime = moment(selected[selected.length - 1].ServiceDate);
      }

      if (selected[0].ServiceEndTime) {
        if (selected[0].ServiceEndTime.endsWith("M")) {
          endTime = moment(
            `${selected[0].ServiceDate} ${selected[0].ServiceEndTime}`,
            "YYYY-MM-DD hh:mm A"
          );
        } else {
          endTime = moment(
            `${selected[0].ServiceDate}T${selected[0].ServiceEndTime}`
          );
        }
      } else {
        endTime = moment(selected[0].ServiceDate);
      }
      let serviceStartDate = startTime
        .clone()
        .subtract(1, "day")
        .format(moment.HTML5_FMT.DATE);
      let serviceEndDate = endTime
        .clone()
        .add(1, "day")
        .format(moment.HTML5_FMT.DATE);

      const body = {
        ClientIds: [data.Client],
        StartDate: serviceStartDate,
        EndDate: serviceEndDate,
      };

      const response = await getServicesForClientsByDates(body);

      if (response.code === "no-content") {
        return;
      }

      const existingServices = response.clients[0].services;

      const overlap = selected.filter((s) => {
        const otherRecordedServices = existingServices.filter((es) => {
          const sStart = moment(`${es.start.user}`);
          const sEnd = moment(`${es.end.user}`);

          if (startTime.isSameOrAfter(sStart) && startTime.isBefore(sEnd)) {
            return true;
          } else if (endTime.isSameOrBefore(sEnd) && endTime.isAfter(sStart)) {
            return true;
          }

          return false;
        });

        return otherRecordedServices.length > 0;
      });

      return overlap.length > 0;
    },
    [services, selectedServices, data.Client]
  );

  const handleConfirmService = async (service) => {
    const hasDuplicate = await validateServiceDate(service);

    if (hasDuplicate) {
      setErrors((e) => ({
        ...e,
        [service.RecordedServiceId]:
          "Invalid timeframe. Services cannot overlap an existing service already in the system.",
      }));
      return;
    }

    const obj = {
      RecordedServiceId: service.RecordedServiceId, // int
      ConfirmedById: safyInfo.user.safyInfo.SOARUserId, // Guid
      ConfirmedByName: `${safyInfo.user.surname}, ${safyInfo.user.givenName}`, // String
    };

    const toastId = toast.loading("Confirming service...");
    const response = await confirmService(obj);
    if (response.code === "success") {
      toast.update(toastId, {
        render: "Service has been confirmed.",
        type: toast.TYPE.SUCCESS,
        isLoading: false,
        closeOnClick: true,
        autoClose: 5000,
      });
      setServices((s) => {
        const arr = [...s];
        arr.splice(
          arr.findIndex(
            (item) => item.RecordedServiceId === service.RecordedServiceId
          ),
          1
        );
        return arr;
      });
    } else if (response.code === "fail") {
      toast.update(toastId, {
        render: "An error has occurred.",
        type: toast.TYPE.ERROR,
        isLoading: false,
        closeOnClick: true,
        autoClose: 5000,
      });
    }
  };

  const handleConfirmSelected = async () => {
    const hasDuplicate = await validateServiceDate();

    if (hasDuplicate) {
      setErrors((e) => ({
        ...e,
        Duplicate:
          "Invalid timeframe. Services cannot overlap an existing service already in the system.",
      }));
      return;
    }

    const obj = {
      RecordedServiceIds: selectedServices, // Array of Intergers
      ConfirmedById: safyInfo.user.safyInfo.SOARUserId, // Guid
      ConfirmedByName: `${safyInfo.user.surname}, ${safyInfo.user.givenName}`, // String
    };

    const toastId = toast.loading("Confirming services...");
    const response = await confirmServices(obj);
    if (response.code === "success") {
      toast.update(toastId, {
        render: "Services have been confirmed.",
        type: toast.TYPE.SUCCESS,
        isLoading: false,
        closeOnClick: true,
        autoClose: 5000,
      });
      setServices((s) =>
        s.filter((x) => selectedServices.indexOf(x.RecordedServiceId) === -1)
      );
      setSelectedServices([]);
    } else if (response.code === "fail") {
      toast.update(toastId, {
        render: "An error has occurred.",
        type: toast.TYPE.ERROR,
        isLoading: false,
        closeOnClick: true,
        autoClose: 5000,
      });
    }
  };

  // Show button when page is scorlled upto given distance
  const toggleVisibility = () => {
    if (window.pageYOffset > 1) {
      setFloatingButtonsVisible(true);
    } else {
      setFloatingButtonsVisible(false);
    }
  };

  useEffect(() => {
    window.addEventListener("scroll", toggleVisibility);

    return () => {
      window.removeEventListener("scroll", toggleVisibility);
    };
  }, []);

  const getDxForService = (service) => {
    return clientDx.filter((d) => {
      if (
        getFieldText(d.ccx_clientprogram) !== `{${service.ClientProgramId}}`
      ) {
        return false;
      }

      const effDate = moment(getFieldUTC(d.ccx_effdate)).startOf("day");
      const serviceDate = moment(service.ServiceDate);

      if (effDate.isAfter(serviceDate)) {
        return false;
      }

      if (d.ccx_expdate) {
        const expDate = moment(getFieldUTC(d.ccx_expdate)).startOf("day");

        if (expDate.isBefore(serviceDate)) {
          return false;
        }
      }

      if (service.ClientProgramName.includes("Alabama")) {
        const diagnosisCheck = diagnosisLookup.find(
          (dl) => dl.DiagnosisCode === d["dx.ccx_code"]
        );

        if (!diagnosisCheck) {
          return false;
        }
      }

      return true;
    });
  };

  const approvalCount = useMemo(() => {
    return services.filter((s) => s.Active && !s.Draft).length;
  }, [services]);

  const serviceMessages = useMemo(() => {
    if (!data.SelectedService) {
      return [];
    }

    const selectedService = services.find(
      (s) => s.RecordedServiceId === data.SelectedService
    );

    if (!selectedService) {
      return [];
    }

    return selectedService.messages
      .map((m) => ({
        DiscussionId: m.discussionId,
        DiscussionTitle: m.discussionTitle,
        DiscussionBody: m.discussionBody,
        TeamMemberId: m.teamMemberId,
        TeamMemberName: m.teamMemberName,
        FosterParentId: m.fosterParentId,
        FosterParentName: m.fosterParentName,
        CreatedOn: m.createdOn,
        CreatedById: m.createdById,
        CreatedByName: m.createdByName,
        RecordedServiceId: m.recordedServiceId,
        ClientId: m.clientId,
        ClientName: selectedService.ClientName,
        DiscussionStatus: m.discussionStatus,
        DiscussionItemStatus: m.discussionItemStatus,
        Responses: selectedService.messages.length - 1,
      }))
      .sort((a, b) => moment(b.CreatedOn) - moment(a.CreatedOn));
  }, [data.SelectedService, services]);

  const [viewportWidth, setViewportWidth] = useState(
    Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0)
  );

  const [viewportHeight, setViewportHeight] = useState(
    Math.max(
      document.documentElement.clientHeight || 0,
      window.innerHeight || 0
    )
  );

  useEffect(() => {
    const getViewportDimensions = () => {
      setViewportWidth(
        Math.max(
          document.documentElement.clientWidth || 0,
          window.innerWidth || 0
        )
      );
      setViewportHeight(
        Math.max(
          document.documentElement.clientHeight || 0,
          window.innerHeight || 0
        )
      );
    };

    window.addEventListener("resize", getViewportDimensions, true);
    return () => window.removeEventListener("resize", getViewportDimensions);
  }, []);

  const handleDeactivateClicked = (service) => {
    setServiceToDeactivate(service);
  };

  return (
    <>
      {serviceUnderReview ? (
        <Modal show centered animation={false}>
          <Modal.Header>
            <Modal.Title>Review Service</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div>
              {serviceUnderReview.ClientName} |
              {moment(serviceUnderReview.ServiceDate).format("L")}
            </div>
            <FormRadioButton
              name="Type"
              value={serviceUnderReview.Type}
              options={reviewTypeOptions}
              onChange={handleReviewChange}
              error={errors.Type}
            />
            <FormTextArea
              name="Message"
              value={serviceUnderReview.Message}
              onChange={handleReviewChange}
              error={errors.Message}
              rows="5"
            />
          </Modal.Body>
          <Modal.Footer>
            <div className="clearfix">
              <div className="float-start">
                <button className="btn btn-success me-2" onClick={handleSend}>
                  Send
                </button>
              </div>
              <div className="float-end">
                <button
                  className="btn btn-danger"
                  onClick={() => {
                    setServiceUnderReview();
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </Modal.Footer>
        </Modal>
      ) : null}
      {serviceToDeactivate ? (
        <Modal
          id="deactivate_modal"
          show={serviceToDeactivate !== null}
          animation={false}
        >
          <Modal.Header className="bg-safy-info text-white">
            <Modal.Title>Deactivate Approval Note</Modal.Title>
          </Modal.Header>
          <Modal.Body className="bg-safy2-4">
            <div>
              <b>Home:</b> {serviceToDeactivate.AssociatedHomeName}
            </div>
            <div>
              <b>Client:</b> {serviceToDeactivate.ClientName}
            </div>
            <div className="mb-3">
              <b>
                Service Date
                {serviceToDeactivate.ServiceStartTime ? "/Time" : ""}:
              </b>{" "}
              {moment(serviceToDeactivate.ServiceDate).format("L")}
              {serviceToDeactivate.ServiceStartTime
                ? ` | ${moment(
                    serviceToDeactivate.ServiceStartTime,
                    "HH:mm"
                  ).format("h:mm A")} - ${moment(
                    serviceToDeactivate.ServiceEndTime,
                    "HH:mm"
                  ).format("h:mm A")}`
                : null}
            </div>
            <div className="fw-bold">
              Once deactivated, no additional actions can be taken.
              <br />
              How would you like to proceed?
            </div>
          </Modal.Body>
          <Modal.Footer className="bg-safy2-4">
            <button
              className="btn btn-danger"
              onClick={() => {
                setServiceToDeactivate(null);
              }}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={() => {
                deactivateService(serviceToDeactivate.RecordedServiceId);
                setServiceToDeactivate(null);
                setServices((s) => {
                  const arr = [...s];
                  arr.splice(
                    arr.findIndex(
                      (item) =>
                        item.RecordedServiceId ===
                        serviceToDeactivate.RecordedServiceId
                    ),
                    1
                  );
                  return arr;
                });
              }}
            >
              Deactivate
            </button>
          </Modal.Footer>
        </Modal>
      ) : null}
      <div className="row">
        <div className="col">
          <FormSelect
            name="Division"
            label="Division:"
            value={data.Division}
            onChange={handleDivisionChange}
            options={divisionOptions}
          />
        </div>
        <div className="col">
          <FormSelect
            name="Client"
            label="Client:"
            value={data.Client}
            onChange={handleClientChange}
            options={clientOptions}
            placeholder={loadStatus.loadingClients ? "Loading..." : ""}
          />
        </div>
      </div>
      {loadStatus.loadingServices ? (
        <div className="d-flex justify-content-center align-items-center">
          <h1>Loading services...</h1>
          <Lottie
            animationData={loadingDots.default}
            loop={true}
            autoplay={true}
            rendererSettings={{
              preserveAspectRatio: "xMidYMid slice",
            }}
          />
        </div>
      ) : services.length > 0 ? (
        <>
          <div className="h5 mb-2">My Approvals ({approvalCount})</div>
          <div className="row justify-content-between">
            <div className="col-auto">
              {approvalCount > 0 ? (
                <div className="d-flex mb-2">
                  {selectedServices.length === approvalCount ? (
                    <button
                      name="UnselectAll"
                      className="btn btn-primary"
                      onClick={() => {
                        setSelectedServices([]);
                      }}
                    >
                      Unselect All
                    </button>
                  ) : (
                    <button
                      name="SelectAll"
                      className="btn btn-primary"
                      onClick={() => {
                        setSelectedServices(
                          services
                            .filter((s) => s.Active && s.Diagnosis && !s.Draft)
                            .map((s) => `${s.RecordedServiceId}`)
                        );
                      }}
                    >
                      Select All
                    </button>
                  )}
                  <button
                    className="btn btn-primary ms-2"
                    onClick={handleConfirmSelected}
                    disabled={selectedServices.length === 0}
                  >
                    Confirm Selected
                    {selectedServices.length > 0 ? (
                      <> ({selectedServices.length})</>
                    ) : null}
                  </button>
                  {errors.Duplicate && (
                    <div className=" ms-2 alert alert-danger">
                      {errors.Duplicate}
                    </div>
                  )}
                </div>
              ) : null}
            </div>
            <div className="col-auto">
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="show_deactivated"
                  checked={showDeactivated}
                  onChange={(e) => setShowDeactivated(e.currentTarget.checked)}
                />
                <label className="form-check-label" htmlFor="show_deactivated">
                  Show Deactivated
                </label>
              </div>
            </div>
          </div>
          <div className="mb-5">
            {filteredServices
              .filter((s) => !s.Draft || s.messages)
              .map((s, index) => (
                <div
                  key={s.RecordedServiceId}
                  className={`container border-top border-start border-end mb-3${
                    s.messages ? " bg-light" : ""
                  }`}
                >
                  <div className="row">
                    <div
                      className={`col text-white py-3 ${
                        !s.Active
                          ? "bg-secondary border border-4 border-danger"
                          : s.Draft && s.messages
                          ? "bg-secondary"
                          : "bg-safy1-1"
                      }`}
                    >
                      <div className="d-flex h5">
                        {s.Active && !s.Draft ? (
                          <div className="ms-2">
                            <FormCheckbox
                              name={`Service-${index}`}
                              checked={selectedServices.includes(
                                `${s.RecordedServiceId}`
                              )}
                              onChange={() => {
                                handleServiceChange(`${s.RecordedServiceId}`);
                              }}
                              disabled={!s.Diagnosis}
                            />
                          </div>
                        ) : null}
                        <div className="flex-fill">
                          {moment(s.ServiceDate).format("L")}
                          {s.ServiceStartTime
                            ? ` | ${moment(s.ServiceStartTime, "HH:mm").format(
                                "h:mm A"
                              )} - ${moment(s.ServiceEndTime, "HH:mm").format(
                                "h:mm A"
                              )}`
                            : null}
                          {!s.Diagnosis ? (
                            <>
                              {" | "}
                              <span className="text-warning">
                                MISSING DIAGNOSIS
                              </span>
                            </>
                          ) : null}
                          {!s.Active ? (
                            <>
                              {" | "}
                              DEACTIVATED
                            </>
                          ) : null}
                        </div>
                        {s.messages ? (
                          <i
                            className="ms-2 fas fa-message text-warning"
                            onClick={() =>
                              setData((d) => ({
                                ...d,
                                SelectedService: s.RecordedServiceId,
                              }))
                            }
                          ></i>
                        ) : null}
                      </div>
                    </div>
                  </div>
                  <div className="row row-cols-6 row-cols-lg-12 py-2 border-bottom">
                    <div className="col-6 text-decoration-underline">
                      <b>Region/Division/Program:</b> {s.ClientProgramName}
                    </div>
                    <div className="col-3 text-decoration-underline">
                      <b>Home:</b> {s.AssociatedHomeName}
                    </div>
                    <div className="col-3 text-decoration-underline">
                      <b>Client:</b> {s.ClientName}
                    </div>
                  </div>
                  {s.Diagnosis ? (
                    <div className="row py-2 border-bottom">
                      <div className="col">
                        <b>Diagnosis:</b>
                      </div>
                      <div className="col">
                        {getDxForService(s).map((dx) => (
                          <div>{dx["dx.ccx_name"]}</div>
                        ))}
                      </div>
                    </div>
                  ) : null}
                  <div className="row py-2 border-bottom">
                    <div className="col">
                      <b>Service Details</b>
                    </div>
                  </div>
                  <div className="row py-2 border-bottom">
                    <div className="col">
                      <div className="d-flex">
                        <div className="text-decoration-underline me-3">
                          <b>Service Setting:</b> {s.ServiceSettingText}
                        </div>
                        <div className="text-decoration-underline">
                          <b>Units for Service:</b> {s.ActualUnitsForService}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row py-2 border-bottom">
                    <div className="col">
                      <b>Service Note</b>
                    </div>
                  </div>
                  {s.note?.map((n) => (
                    <div key={n.id} className="row py-2 border-bottom">
                      <div className="col">
                        <b>{n.label}</b>
                      </div>
                      <div className="col">{n.value}</div>
                    </div>
                  ))}
                  {s.Active && !s.Draft ? (
                    <div className="row py-2 border-bottom">
                      <div className="col-auto">
                        <button
                          name="Confirm"
                          className="btn btn-primary me-2"
                          onClick={() => {
                            handleConfirmService(s);
                          }}
                          disabled={!s.Diagnosis}
                        >
                          Confirm
                        </button>
                      </div>
                      <div className="col-auto">
                        <button
                          name="Review"
                          className="btn btn-primary"
                          onClick={() => {
                            setServiceUnderReview(s);
                          }}
                        >
                          Review
                        </button>
                      </div>
                      <div className="col-auto">
                        {errors[s.RecordedServiceId] && (
                          <div className="ms-2 alert alert-danger">
                            {errors[s.RecordedServiceId]}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : s.Active && s.messages.length > 0 ? (
                    <>
                      <div className="row py-2 border-bottom">
                        <div className="col-auto">
                          <button
                            name="Deactivate"
                            className="btn btn-primary"
                            onClick={() => {
                              handleDeactivateClicked(s);
                            }}
                          >
                            Deactivate
                          </button>
                        </div>
                        <div className="col-auto">
                          {errors[s.RecordedServiceId] && (
                            <div className="ms-2 alert alert-danger">
                              {errors[s.RecordedServiceId]}
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  ) : null}
                  {s.messages &&
                  data.SelectedService === s.RecordedServiceId ? (
                    <Modal
                      show
                      animation={false}
                      onHide={() =>
                        setData((d) => ({ ...d, SelectedService: null }))
                      }
                      size="lg"
                      centered
                      scrollable
                    >
                      <Modal.Header
                        closeButton
                        className="bg-safy-info text-white"
                      >
                        <Modal.Title>Service Messages</Modal.Title>
                      </Modal.Header>
                      <Modal.Body>
                        <div
                          className={`safy-inbox approval-inbox${
                            viewportWidth < 576 || viewportHeight < 900
                              ? " small-inbox"
                              : ""
                          }`}
                        >
                          <ServiceMessages
                            serviceMessages={serviceMessages}
                            unreadClients={[]}
                            initialData={{
                              recordedServiceId: s.RecordedServiceId,
                              client: s.ClientId,
                              discussion: 98099,
                            }}
                            readOnly={!s.Active}
                          />
                        </div>
                      </Modal.Body>
                    </Modal>
                  ) : null}
                </div>
              ))}
            <button className="btn btn-primary" onClick={handleConfirmSelected}>
              Confirm Selected
            </button>
          </div>
        </>
      ) : (
        <>No Services in need of Review...</>
      )}
      {floatingButtonsVisible ? (
        <div className="floating-toolbox">
          <div className="clearfix mb-3">
            <div className="float-start">
              <i
                className={`fas fa-2x me-2 mt-2 fa-chevron-${
                  toolboxCollapsed ? "left" : "right"
                }`}
                onClick={() => {
                  setToolboxCollapsed(!toolboxCollapsed);
                }}
              ></i>
            </div>
            {!toolboxCollapsed && (
              <>
                <div className="float-end ms-2">
                  <ScrollToTop />
                </div>
                <div className="float-end">
                  <span className="fa-stack fa-xl">
                    <i className="fa fa-circle fa-stack-2x text-safy-primary"></i>
                    <span className="fa fa-stack-1x text-white">
                      <span
                        style={{
                          display: "block",
                        }}
                      >
                        {selectedServices.length}
                      </span>
                    </span>
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
      ) : null}
    </>
  );
};

export default FPApprovals;
